import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Typography,
  Button,
  TextField,
  Box,
  CircularProgress,
} from "@mui/material";
import axios from "axios";
import Swal from "sweetalert2";
import { encrypt, decrypt } from "../../Encrypdecryp/Crypt";
import JsonEditor from "../../components/JsonEditor";
import DragAndDrop from "./DragAndDrop";
import FileCard from "./FileCard";
import PdfPreview from "../../components/PdfPreview";
import * as pdfjsLib from "pdfjs-dist";
import ProgressBar from "./ProgressBar";
import "./ExistingTransaction.css";
import _ from "lodash";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const ExistingTransaction = ({
  userRole,
  transactions,
  setTransactions,
  selectedTransaction,
  setSelectedTransaction,
  selectedFileIndex,
  setSelectedFileIndex,
  selectedFileType,
  setSelectedFileType,
  pdfUrl,
  setPdfUrl,
  extractedData,
  setExtractedData,
  originalExtractedData,
  setOriginalExtractedData,
  documentId,
  setDocumentId,
  uploadedFiles,
  setUploadedFiles,
  pollingProgress,
  setPollingProgress,
  pollingReferenceIds,
  setPollingReferenceIds,
  progressComplete,
  setProgressComplete,
  loading,
  setLoading,
  error,
  setError,
  docType,
  setDocType,
  isDataChanged,
  setIsDataChanged,
  navigate,
  fetchThumbnail,
  getThumbnailFilename,
  truncateFileName,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchMessage, setSearchMessage] = useState("");
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [hoveredTransaction, setHoveredTransaction] = useState(null);
  const [previewThumbnails, setPreviewThumbnails] = useState([]);
  const [simulatedProgress, setSimulatedProgress] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [hasFileChanges, setHasFileChanges] = useState(false);
  const [initialFileCount, setInitialFileCount] = useState(0);
  const fileInputRef = useRef(null);

  const handleError = useCallback((error, defaultMessage) => {
    let errorMessage = defaultMessage;
    if (error.response?.data?.message) {
      errorMessage += error.response.data.message;
    } else if (error.message) {
      errorMessage += error.message;
    } else {
      errorMessage += "Unknown error";
    }
    if (error.response?.status === 403) {
      errorMessage = "Access denied: Invalid or expired token. Please log in again.";
      localStorage.removeItem("authToken");
      navigate("/login");
    }
    setError(errorMessage);
    setLoading(false);
    toast.error(errorMessage, {
      position: "bottom-right",
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      className: "toast-error",
    });
  }, [navigate, setError, setLoading]);

  const handleFileSelect = useCallback(async (index, transactionId) => {
    if (isDataChanged) {
      Swal.fire({
        title: "Unsaved Changes",
        text: "You have unsaved changes. Please save or undo your changes before switching files.",
        icon: "warning",
        confirmButtonColor: "#3085d6",
        confirmButtonText: "OK",
      });
      return;
    }
    console.log("handleFileSelect - Transaction ID:", transactionId, "Index:", index);
    const transaction = transactions.find((t) => t.id === transactionId);
    console.log("handleFileSelect - Transaction:", transaction);
    if (!transaction || !transaction.files[index]) {
      setSelectedFileIndex(null);
      setSelectedFileType(null);
      setPdfUrl(null);
      setExtractedData(null);
      setOriginalExtractedData(null);
      setDocumentId(null);
      setLoading(false);
      toast.error("The selected file could not be found.", {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        className: "toast-error",
      });
      return;
    }
    setSelectedFileType("existing");
    setSelectedFileIndex(index);
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("authToken");
      if (!token) throw new Error("No authentication token found.");
      let pdfUrl = transaction.files[index].pdfUrl;
      if (!pdfUrl && transaction.files[index].pdfPath) {
        const fileName = transaction.files[index].pdfPath.split("/storage/")[1];
        const pdfResponse = await axios.get(
          `${API_BASE_URL}/storage/${fileName}`,
          {
            headers: { Authorization: `Bearer ${token}` },
            responseType: "blob",
          }
        );
        pdfUrl = URL.createObjectURL(pdfResponse.data);
      }
      setPdfUrl(pdfUrl);
      const dataResponse = await axios.get(
        `${API_BASE_URL}/transactions/${transactionId}/documents/${transaction.files[index].id}/mongo-data`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const finalizedData = dataResponse.data?.mongo_data?.finalized_data || null;
      setExtractedData(finalizedData);
      setOriginalExtractedData(finalizedData);
      setDocumentId(transaction.files[index].id);
      setIsDataChanged(false);
    } catch (error) {
      handleError(error, "Failed to load PDF or extracted data: ");
      setPdfUrl(null);
      setExtractedData(null);
      setOriginalExtractedData(null);
      setDocumentId(null);
    } finally {
      setLoading(false);
    }
  }, [transactions, setSelectedFileIndex, setSelectedFileType, setPdfUrl, setExtractedData, setOriginalExtractedData, setDocumentId, setLoading, setIsDataChanged, handleError, isDataChanged]);

  useEffect(() => {
    console.log("Transactions state updated:", transactions);
    console.log(
      "Filtered transactions:",
      transactions.filter((t) =>
        t.name.toLowerCase().includes(searchQuery.toLowerCase())
      )
    );
  }, [transactions, searchQuery]);

  useEffect(() => {
    let interval;
    if (
      pollingReferenceIds.size > 0 &&
      simulatedProgress < 100 &&
      uploadedFiles.length > 0
    ) {
      interval = setInterval(() => {
        setSimulatedProgress((prev) => {
          if (prev >= 99) {
            clearInterval(interval);
            return prev;
          }
          return prev + 2;
        });
      }, 100);
    }
    return () => clearInterval(interval);
  }, [pollingReferenceIds.size, uploadedFiles.length]);

  useEffect(() => {
    if (uploadedFiles.length === 0) {
      setSimulatedProgress(0);
      setProgressComplete(false);
      setPollingReferenceIds(new Set());
    }
  }, [uploadedFiles.length, setPollingReferenceIds]);

  useEffect(() => {
    if (selectedTransaction && (!selectedFileIndex || selectedFileIndex === null)) {
      console.log("useEffect - Selected Transaction:", selectedTransaction);
      const transaction = transactions.find((t) => t.id === selectedTransaction);
      console.log("useEffect - Found Transaction:", transaction);
      
      // Set initial file count when transaction is selected
      if (transaction) {
        setInitialFileCount(transaction.files?.length || 0);
        setHasFileChanges(false);
      }
      
      if (uploadedFiles.length > 0) {
        const firstUploadedIndex = 0;
        setSelectedFileType("uploaded");
        setSelectedFileIndex(firstUploadedIndex);
        setPdfUrl(uploadedFiles[firstUploadedIndex].pdfUrl);
        setExtractedData(uploadedFiles[firstUploadedIndex].initialData || null);
        setOriginalExtractedData(uploadedFiles[firstUploadedIndex].initialData || null);
        setDocumentId(uploadedFiles[firstUploadedIndex].documentId || null);
      } else if (transaction?.files?.length > 0) {
        const firstExistingIndex = 0;
        setSelectedFileType("existing");
        setSelectedFileIndex(firstExistingIndex);
        handleFileSelect(firstExistingIndex, selectedTransaction);
      } else {
        setSelectedFileIndex(null);
        setSelectedFileType(null);
        setPdfUrl(null);
        setExtractedData(null);
        setOriginalExtractedData(null);
        setDocumentId(null);
      }
    }
  }, [selectedTransaction, uploadedFiles, transactions, selectedFileIndex, handleFileSelect]);

  // Track file changes
  useEffect(() => {
    if (selectedTransaction) {
      const transaction = transactions.find((t) => t.id === selectedTransaction);
      const currentFileCount = (transaction?.files?.length || 0) + uploadedFiles.length;
      
      if (initialFileCount !== 0 && currentFileCount !== initialFileCount) {
        setHasFileChanges(true);
      }
    }
  }, [selectedTransaction, transactions, uploadedFiles, initialFileCount]);

  const fetchTransactions = async (query, token, limit = 9) => {
    try {
      setLoading(true);
      setSearchMessage("");
      const response = await axios.get(
        `${API_BASE_URL}/advanced_search?query=${encodeURIComponent(query)}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const transactionSetIds = response.data.mongo_data?.transaction_set_ids || [];
      if (transactionSetIds.length === 0) {
        setTransactions([]);
        setSearchMessage("No transactions found.");
        setLoading(false);
        return [];
      }

      const transactionsWithThumbnails = await Promise.all(
        transactionSetIds.slice(0, limit).map(async (id) => {
          try {
            const transactionResponse = await axios.get(
              `${API_BASE_URL}/transaction?transaction_id=${id}`,
              {
                headers: { Authorization: `Bearer ${token}` },
              }
            );
            const transaction = transactionResponse.data;
            let thumbnail = "/default_thumb.jpg";
            if (transaction.documents && transaction.documents.length > 0) {
              const pdfPath = transaction.documents[0].pdf_path;
              const filename = getThumbnailFilename(pdfPath, null);
              if (filename) thumbnail = await fetchThumbnail(filename);
            }
            return {
              id: transaction.id.toString(),
              name: decrypt(transaction.name) || `Transaction ${transaction.id}`,
              thumbnail,
              files: await Promise.all(
                transaction.documents.map(async (doc) => {
                  const filename = getThumbnailFilename(doc.pdf_path, null);
                  const thumbnail = filename
                    ? await fetchThumbnail(filename)
                    : "/default_thumb.jpg";
                  return {
                    id: doc.id.toString(),
                    name: doc.pdf_file_name || doc.pdf_path.split("/").pop(),
                    docType: decrypt(doc.type) || "Unknown",
                    pdfUrl: null,
                    fileData: null,
                    extractedData: null,
                    pdfPath: doc.pdf_path,
                    thumbnail,
                    transaction_set_id: doc.transaction_set_id.toString(),
                    isSaved: true,
                  };
                })
              ),
            };
          } catch (err) {
            console.error(`Error fetching transaction ${id}:`, err);
            return null;
          }
        })
      );

      const validTransactions = transactionsWithThumbnails.filter((t) => t !== null);
      setTransactions(validTransactions);
      localStorage.setItem("transactions", JSON.stringify(validTransactions));
      setSearchMessage(validTransactions.length ? "" : "No valid transactions found.");
      setLoading(false);
      return validTransactions;
    } catch (error) {
      handleError(error, "Unable to search transactions: ");
      return [];
    }
  };

  const fetchSingleTransaction = async (transactionId, token) => {
    try {
      setLoading(true);
      setSearchMessage("");
      const transactionResponse = await axios.get(
        `${API_BASE_URL}/transaction?transaction_id=${transactionId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const transaction = transactionResponse.data;
      let thumbnail = "/default_thumb.jpg";
      if (transaction.documents && transaction.documents.length > 0) {
        const pdfPath = transaction.documents[0].pdf_path;
        const filename = getThumbnailFilename(pdfPath, null);
        if (filename) thumbnail = await fetchThumbnail(filename);
      }
      const updatedTransaction = {
        id: transaction.id.toString(),
        name: decrypt(transaction.name) || `Transaction ${transaction.id}`,
        thumbnail,
        files: await Promise.all(
          transaction.documents.map(async (doc) => {
            const filename = getThumbnailFilename(doc.pdf_path, null);
            const thumbnail = filename
              ? await fetchThumbnail(filename)
              : "/default_thumb.jpg";
            return {
              id: doc.id.toString(),
              name: doc.pdf_file_name || doc.pdf_path.split("/").pop(),
              docType: decrypt(doc.type) || "Unknown",
              pdfUrl: null,
              fileData: null,
              extractedData: null,
              pdfPath: doc.pdf_path,
              thumbnail,
              transaction_set_id: doc.transaction_set_id.toString(),
              isSaved: true,
            };
          })
        ),
      };
      return updatedTransaction;
    } catch (error) {
      handleError(error, "Unable to fetch transaction: ");
      return null;
    } finally {
      setLoading(false);
    }
  };

  const handleTransactionSelect = async (transactionId) => {
    if (isDataChanged) {
      Swal.fire({
        title: "Unsaved Changes",
        text: "You have unsaved changes. Please save or undo your changes before switching transactions.",
        icon: "warning",
        confirmButtonColor: "#3085d6",
        confirmButtonText: "OK",
      });
      return;
    }
    console.log("handleTransactionSelect - Selecting transaction with ID:", transactionId);
    setSelectedTransaction(transactionId);
    setUploadedFiles([]);
    setExtractedData(null);
    setOriginalExtractedData(null);
    setDocumentId(null);
    setLoading(true);
    setError(null);
    setIsDataChanged(false);
    setHasFileChanges(false);
    
    const token = localStorage.getItem("authToken");
    if (!token) {
      toast.error("No authentication token found. Please log in again.", {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        className: "toast-error",
      });
      localStorage.removeItem("authToken");
      navigate("/login");
      setLoading(false);
      return;
    }
    const transaction = transactions.find((t) => t.id === transactionId);
    console.log("handleTransactionSelect - Found transaction:", transaction);
    
    // Set initial file count
    if (transaction) {
      setInitialFileCount(transaction.files?.length || 0);
    }
    
    if (!transaction) {
      toast.error("The specified transaction could not be found.", {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        className: "toast-error",
      });
      setSelectedFileIndex(null);
      setSelectedFileType(null);
      setPdfUrl(null);
      setLoading(false);
      return;
    }
    if (transaction.files && transaction.files.length > 0) {
      console.log("handleTransactionSelect - Files for selected transaction:", transaction.files);
      setSelectedFileIndex(0);
      setSelectedFileType("existing");
      await handleFileSelect(0, transactionId);
    } else {
      console.log("handleTransactionSelect - No files found for selected transaction");
      setSelectedFileIndex(null);
      setSelectedFileType(null);
      setPdfUrl(null);
      setExtractedData(null);
      setOriginalExtractedData(null);
      setDocumentId(null);
      toast.info("This transaction has no files to display.", {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        className: "toast-success",
      });
      setLoading(false);
    }
  };

  const handleMouseEnter = async (transaction, index) => {
    setHoveredTransaction(transaction.id);
    const thumbnails = await Promise.all(
      transaction.files.map(async (file, fileIndex) => {
        let thumbnail = file.thumbnail || "/default_thumb.jpg";
        if (
          !thumbnail.startsWith("blob:") &&
          !thumbnail.startsWith("/default_thumb.jpg")
        ) {
          const filename = getThumbnailFilename(file.pdfPath, null);
          if (filename) thumbnail = await fetchThumbnail(filename);
        }
        return { name: file.name, thumbnail, fileIndex };
      })
    );
    setPreviewThumbnails(thumbnails);
  };

  const handleMouseLeave = () => {
    setHoveredTransaction(null);
    setPreviewThumbnails([]);
  };

  const handleThumbnailClick = (transactionId, fileIndex) => {
    if (isDataChanged) {
      Swal.fire({
        title: "Unsaved Changes",
        text: "You have unsaved changes. Please save or undo your changes before switching files.",
        icon: "warning",
        confirmButtonColor: "#3085d6",
        confirmButtonText: "OK",
      });
      return;
    }
    setSelectedTransaction(transactionId);
    handleFileSelect(fileIndex, transactionId);
  };

  const handleUploadedFileSelect = (index) => {
    if (isDataChanged) {
      Swal.fire({
        title: "Unsaved Changes",
        text: "You have unsaved changes. Please save or undo your changes before switching files.",
        icon: "warning",
        confirmButtonColor: "#3085d6",
        confirmButtonText: "OK",
      });
      return;
    }
    setSelectedFileType("uploaded");
    setSelectedFileIndex(index);
    setPdfUrl(uploadedFiles[index].pdfUrl);
    const fileData = uploadedFiles[index].initialData || null;
    setExtractedData(fileData);
    setOriginalExtractedData(fileData);
    setDocumentId(uploadedFiles[index].documentId || null);
    setIsDataChanged(false);
  };

  const handleSuggestionClick = async (suggestion) => {
    setSearchQuery(suggestion);
    setSearchSuggestions([]);
    setIsSearchActive(true);
    const token = localStorage.getItem("authToken");
    if (!token) {
      toast.error("No authentication token found. Please log in again.", {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        className: "toast-error",
      });
      localStorage.removeItem("authToken");
      navigate("/login");
      return;
    }
    await fetchTransactions(suggestion, token);
  };

  const handleJsonChange = (newData) => {
    setExtractedData(newData);
    const isDataUnchanged = JSON.stringify(newData) === JSON.stringify(originalExtractedData);
    setIsDataChanged(!isDataUnchanged);
    setIsEditing(true);
    if (
      selectedFileIndex !== null &&
      selectedFileType === "uploaded" &&
      uploadedFiles[selectedFileIndex]
    ) {
      setUploadedFiles((prev) =>
        prev.map((file, index) =>
          index === selectedFileIndex
            ? { ...file, initialData: newData }
            : file
        )
      );
    }
  };

  const handleUpdateData = async () => {
    if (!selectedTransaction || selectedFileIndex === null) {
      toast.warning("No transaction or file selected to update.", {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        className: "toast-error",
      });
      return;
    }
    if (!extractedData) {
      toast.warning("No finalized data available to update.", {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        className: "toast-error",
      });
      return;
    }
    try {
      const token = localStorage.getItem("authToken");
      if (!token) throw new Error("No authentication token found.");
      const transaction = transactions.find((t) => t.id === selectedTransaction);
      if (!transaction) throw new Error("Selected transaction not found.");
      const documentId =
        selectedFileType === "existing"
          ? transaction.files[selectedFileIndex]?.id
          : uploadedFiles[selectedFileIndex]?.documentId;
      if (!documentId) throw new Error("Document ID not found for selected file.");
      const finalizedData = extractedData?.mongo_data?.finalized_data || extractedData;
      await axios.put(
        `${API_BASE_URL}/transactions/${selectedTransaction}/documents/${documentId}/update-data`,
        { finalized_data: finalizedData },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      toast.success("Data updated successfully.", {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        className: "toast-success",
      });
      if (selectedFileType === "existing") {
        setTransactions((prev) =>
          prev.map((t) =>
            t.id === selectedTransaction
              ? {
                  ...t,
                  files: t.files.map((f, index) =>
                    index === selectedFileIndex
                      ? { ...f, extractedData: finalizedData }
                      : f
                  ),
                }
              : t
          )
        );
      } else {
        setUploadedFiles((prev) =>
          prev.map((file, index) =>
            index === selectedFileIndex
              ? { ...file, initialData: finalizedData, isSaved: true }
              : file
          )
        );
      }
      setOriginalExtractedData(finalizedData);
      setIsDataChanged(false);
      setIsEditing(false);
    } catch (error) {
      handleError(error, "Failed to update the data: ");
    }
  };

  const handleCancelUpdate = () => {
    setExtractedData(originalExtractedData);
    setIsDataChanged(false);
    setIsEditing(false);
  };

  const handleDeleteDocument = async (transactionId, documentId, fileName) => {
    Swal.fire({
      title: "Are you sure?",
      text: `Do you want to delete the document "${fileName}"? This action cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const token = localStorage.getItem("authToken");
          if (!token) throw new Error("No authentication token found.");
          const transaction = transactions.find((t) => t.id === transactionId);
          const file = transaction?.files.find((f) => f.id === documentId);
          if (file && file.extractedData) {
            const encryptedToken = encrypt(token);
            await axios.post(
              `${API_BASE_URL}/documents/confirm`,
              {
                transaction_set_id: transactionId,
                document_id: documentId,
                finalized_data: file.extractedData,
                token: encryptedToken,
              },
              {
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${token}`,
                },
              }
            );
          }
          await axios.delete(
            `${API_BASE_URL}/delete/transaction/${transactionId}/document/${documentId}/`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );
          
          // Mark that files have changed
          setHasFileChanges(true);
          
          setTransactions((prev) =>
            prev
              .map((t) =>
                t.id === transactionId
                  ? {
                      ...t,
                      files: t.files.filter((f) => f.id !== documentId),
                    }
                  : t
              )
              .filter((t) => t.files.length > 0)
          );
          localStorage.setItem("transactions", JSON.stringify(transactions));
          if (
            selectedFileIndex !== null &&
            documentId === transactions.find((t) => t.id === transactionId)?.files[selectedFileIndex]?.id
          ) {
            setSelectedFileIndex(null);
            setSelectedFileType(null);
            setPdfUrl(null);
            setExtractedData(null);
            setOriginalExtractedData(null);
            setDocumentId(null);
            setIsDataChanged(false);
            setIsEditing(false);
          }
          toast.success("The document has been deleted.", {
            position: "bottom-right",
            autoClose: 1500,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            className: "toast-success",
          });
        } catch (error) {
          handleError(error, "Failed to delete the document: ");
        }
      }
    });
  };

  const handleDeleteUploadedFile = async (fileId, fileName, index) => {
    Swal.fire({
      title: "Are you sure?",
      text: `Do you want to delete the file "${fileName}"? This action cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        const file = uploadedFiles[index];
        let successMessage = "The file has been removed locally.";
        if (file.documentId && file.transaction_set_id) {
          try {
            const token = localStorage.getItem("authToken");
            if (!token) throw new Error("No authentication token found.");
            if (file.initialData) {
              const encryptedToken = encrypt(token);
              await axios.post(
                `${API_BASE_URL}/documents/confirm`,
                {
                  transaction_set_id: file.transaction_set_id,
                  document_id: file.documentId,
                  finalized_data: file.initialData,
                  token: encryptedToken,
                },
                {
                  headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                  },
                }
              );
            }
            await axios.delete(
              `${API_BASE_URL}/delete/transaction/${file.transaction_set_id}/document/${file.documentId}/`,
              {
                headers: { Authorization: `Bearer ${token}` },
              }
            );
            successMessage = "The file has been deleted from the server.";
          } catch (error) {
            handleError(error, "Failed to delete the document: ");
            return;
          }
        }
        
        // Mark that files have changed
        setHasFileChanges(true);
        
        const updatedUploadedFiles = uploadedFiles.filter((f, i) => i !== index);
        setUploadedFiles(updatedUploadedFiles);
        if (selectedFileIndex === index) {
          if (updatedUploadedFiles.length > 0) {
            const newIndex =
              index >= updatedUploadedFiles.length ? updatedUploadedFiles.length - 1 : index;
            setSelectedFileType("uploaded");
            setSelectedFileIndex(newIndex);
            setPdfUrl(updatedUploadedFiles[newIndex].pdfUrl);
            setExtractedData(updatedUploadedFiles[newIndex].initialData || null);
            setOriginalExtractedData(updatedUploadedFiles[newIndex].initialData || null);
            setDocumentId(updatedUploadedFiles[newIndex].documentId || null);
            setIsEditing(false);
          } else {
            const transaction = transactions.find((t) => t.id === selectedTransaction);
            if (transaction?.files.length > 0) {
              setSelectedFileType("existing");
              setSelectedFileIndex(0);
              handleFileSelect(0, selectedTransaction);
            } else {
              setSelectedFileIndex(null);
              setSelectedFileType(null);
              setPdfUrl(null);
              setExtractedData(null);
              setOriginalExtractedData(null);
              setDocumentId(null);
              setIsEditing(false);
            }
          }
        } else if (selectedFileIndex !== null && index < selectedFileIndex) {
          setSelectedFileIndex(selectedFileIndex - 1);
        }
        toast.success(successMessage, {
          position: "bottom-right",
          autoClose: 1500,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          className: "toast-success",
        });
      }
    });
  };

  const handleFileChange = async (selectedFiles) => {
    if (
      !selectedFiles.length ||
      !Array.from(selectedFiles).every((file) => file.type === "application/pdf")
    ) {
      toast.error("Please upload only PDF files.", {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        className: "toast-error",
      });
      return;
    }
    if (selectedFiles.length > 10) {
      toast.warning("You can upload up to 10 files at a time.", {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        className: "toast-error",
      });
      return;
    }
    if (!selectedTransaction) {
      toast.warning("Please select a transaction before uploading files.", {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        className: "toast-error",
      });
      return;
    }
    const transaction = transactions.find((t) => t.id === selectedTransaction);
    const existingFileNames = transaction
      ? [
          ...transaction.files.map((f) => f.name.toLowerCase()),
          ...uploadedFiles.map((f) => f.name.toLowerCase()),
        ]
      : [];
    const newFileNames = Array.from(selectedFiles).map((f) => f.name.toLowerCase());
    const duplicates = newFileNames.filter((name) => existingFileNames.includes(name));
    if (duplicates.length > 0) {
      toast.warning(
        `The following files are already in the transaction: ${duplicates.join(", ")}. Please upload different files.`,
        {
          position: "bottom-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          className: "toast-error",
        }
      );
      return;
    }
    
    // Mark that files have changed
    setHasFileChanges(true);
    
    setSimulatedProgress(0);
    setProgressComplete(false);
    setPollingProgress(0);
    setLoading(true);
    setError(null);
    try {
      const response = await extractPdf(selectedTransaction, selectedFiles);
      setPollingReferenceIds((prev) => new Set([...prev, response.reference_id]));
      const updatedUploadedFiles = await Promise.all(
        Array.from(selectedFiles).map(async (file, index) => {
          const pdfUrl = URL.createObjectURL(file);
          const reader = new FileReader();
          return new Promise((resolve) => {
            reader.onload = async () => {
              const fileData = reader.result;
              const thumbnail = await generateThumbnail(fileData, response.reference_id, file.name);
              const fileId = response.document_ids
                ? `${response.reference_id}-${index}`
                : `temp-${response.reference_id}-${index}`;
              resolve({
                id: fileId,
                name: file.name,
                docType: docType,
                pdfUrl,
                fileData,
                initialData: null,
                thumbnail,
                documentId: response.document_ids
                  ? response.document_ids[index]
                  : response.document_id,
                transaction_set_id: response.transaction_set_id || selectedTransaction,
                reference_id: response.reference_id,
                isSaved: false,
              });
            };
            reader.readAsDataURL(file);
          });
        })
      );
      setUploadedFiles((prev) => {
        const newFiles = [...prev, ...updatedUploadedFiles];
        const newFileIndex = prev.length;
        setSelectedFileType("uploaded");
        setSelectedFileIndex(newFileIndex);
        setPdfUrl(updatedUploadedFiles[0].pdfUrl);
        setExtractedData(updatedUploadedFiles[0].initialData || null);
        setOriginalExtractedData(updatedUploadedFiles[0].initialData || null);
        setDocumentId(updatedUploadedFiles[0].documentId || null);
        return newFiles;
      });
      setLoading(false);
      toast.success("PDF files have been uploaded and are being processed.", {
        position: "bottom-right",
        autoClose: 1500,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        className: "toast-success",
      });
    } catch (err) {
      handleError(err, "Failed to process PDFs: ");
    }
  };

  const extractPdf = async (transactionId, files) => {
    const formData = new FormData();
    Array.from(files).forEach((file) => formData.append("pdf_files", file));
    const token = localStorage.getItem("authToken");
    if (!token) throw new Error("No authentication token found.");
    const params = new URLSearchParams();
    params.append("transaction_set_id", transactionId);
    params.append("doc_type", docType);
    const url = `${API_BASE_URL}/documents/extract?${params.toString()}`;
    const response = await axios.post(url, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  };

  const generateThumbnail = async (fileData, referenceId, fileName) => {
    try {
      if (referenceId && fileName) {
        const token = localStorage.getItem("authToken");
        const statusResponse = await axios.get(
          `${API_BASE_URL}/documents/status/${referenceId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        const document = statusResponse.data.documents?.find(
          (doc) => doc.pdf_file_name === fileName
        );
        if (document && document.thumbnail_path) {
          return await fetchThumbnail(document.thumbnail_path);
        }
      }
      const pdf = await pdfjsLib.getDocument({
        data: atob(fileData.split(",")[1]),
      }).promise;
      const page = await pdf.getPage(1);
      const viewport = page.getViewport({ scale: 0.5 });
      const canvas = document.createElement("canvas");
      canvas.height = viewport.height;
      canvas.width = viewport.width;
      const context = canvas.getContext("2d");
      await page.render({ canvasContext: context, viewport }).promise;
      return canvas.toDataURL("image/jpeg");
    } catch (error) {
      console.error("Error generating thumbnail:", error);
      return "/default_thumb.jpg";
    }
  };

  const handleCompleteTransaction = async () => {
    if (!selectedTransaction) {
      toast.warn("No transaction selected.", {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        className: "toast-error",
      });
      return;
    }

    if (!hasFileChanges) {
      toast.info("No file changes detected. Transaction is already complete.", {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        className: "toast-success",
      });
      return;
    }

    try {
      const token = localStorage.getItem("authToken");
      if (!token) throw new Error("No authentication token found.");

      console.log(`Triggering validate-all endpoint for transaction ${selectedTransaction}`);
      
      const validateResponse = await axios.post(
        `${API_BASE_URL}/documents/validate-all`,
        { transaction_set_id: selectedTransaction },
        { 
          headers: { Authorization: `Bearer ${token}` },
          timeout: 10000 
        }
      );
      
      console.log(`Validate-all response:`, validateResponse.data);
      
      toast.success("Transaction validation completed successfully!", {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        className: "toast-success",
      });

      // Reset state and navigate back
      await handleBackToTransactions();
      
    } catch (error) {
      const errorMessage = error.response?.data?.detail
        ? Array.isArray(error.response.data.detail)
          ? error.response.data.detail.map((d) => d.msg).join("; ")
          : error.response.data.detail
        : error.response?.data?.message || error.message;
      
      console.error(`Validate-all error:`, errorMessage);
      
      toast.error(`Failed to complete transaction: ${errorMessage}`, {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        className: "toast-error",
      });
    }
  };

  const handleBackToTransactions = async () => {
    if (isDataChanged) {
      Swal.fire({
        title: "Unsaved Changes",
        text: "You have unsaved changes. Please save or undo your changes before navigating away.",
        icon: "warning",
        confirmButtonColor: "#3085d6",
        confirmButtonText: "OK",
      });
      return;
    }
    const token = localStorage.getItem("authToken");
    if (!token) {
      toast.error("No authentication token found. Please log in again.", {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        className: "toast-error",
      });
      localStorage.removeItem("authToken");
      navigate("/login");
      return;
    }
    let updatedTransactions = [...transactions];
    if (selectedTransaction) {
      const updatedTransaction = await fetchSingleTransaction(selectedTransaction, token);
      if (updatedTransaction) {
        updatedTransactions = updatedTransactions.filter((t) => t.id !== selectedTransaction);
        updatedTransactions = [updatedTransaction, ...updatedTransactions];
        updatedTransactions = updatedTransactions.slice(0, 9);
        setTransactions(updatedTransactions);
        localStorage.setItem("transactions", JSON.stringify(updatedTransactions));
      }
    }
    setSelectedTransaction("");
    setUploadedFiles([]);
    setSelectedFileIndex(null);
    setSelectedFileType(null);
    setPdfUrl(null);
    setExtractedData(null);
    setOriginalExtractedData(null);
    setDocumentId(null);
    setIsDataChanged(false);
    setSimulatedProgress(0);
    setProgressComplete(false);
    setIsEditing(false);
    setHasFileChanges(false);
    setInitialFileCount(0);
  };

  const handleSaveDocument = useCallback(async (file, index) => {
    if (!file.initialData || !file.documentId) {
      toast.warning("Please wait for document processing to complete.", {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        className: "toast-error",
      });
      return;
    }
    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        navigate("/login");
        throw new Error("No authentication token found.");
      }
      const encryptedToken = encrypt(token);
      const dataToSend =
        selectedFileType === "uploaded" &&
        selectedFileIndex === index &&
        extractedData
          ? extractedData
          : file.initialData;
      const requestBody = {
        transaction_set_id: file.transaction_set_id,
        document_id: file.documentId,
        finalized_data: dataToSend,
        token: encryptedToken,
      };
      await axios.post(
        `${API_BASE_URL}/documents/confirm`,
        requestBody,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setUploadedFiles((prev) =>
        prev.map((f, i) =>
          i === index
            ? { ...f, initialData: dataToSend, isSaved: true }
            : f
        )
      );
      setTransactions((prev) =>
        prev.map((t) =>
          t.id === file.transaction_set_id
            ? {
                ...t,
                files: [
                  ...t.files,
                  {
                    id: file.documentId,
                    name: file.name,
                    docType: file.docType,
                    pdfUrl: file.pdfUrl,
                    fileData: null,
                    extractedData: dataToSend,
                    pdfPath: file.pdfPath || `storage/${file.name}`,
                    thumbnail: file.thumbnail,
                    transaction_set_id: file.transaction_set_id,
                    isSaved: true,
                  },
                ],
              }
            : t
        )
      );
      if (selectedFileType === "uploaded" && selectedFileIndex === index) {
        setExtractedData(dataToSend);
        setOriginalExtractedData(dataToSend);
        setDocumentId(file.documentId);
        setIsDataChanged(false);
        setIsEditing(false);
      }
      toast.success("Document has been saved!", {
        position: "bottom-right",
        autoClose: 1500,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        className: "toast-success",
      });
    } catch (error) {
      handleError(error, "Failed to save the document: ");
    }
  }, [selectedFileType, selectedFileIndex, extractedData, navigate, handleError]);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchSuggestions([]);
      setSearchMessage("");
      setIsSearchActive(false);
      return;
    }
    const debounceTimer = setTimeout(async () => {
      try {
        const token = localStorage.getItem("authToken");
        if (!token) throw new Error("No authentication token found.");
        setLoading(true);
        setSearchMessage("");
        const response = await axios.get(
          `${API_BASE_URL}/advanced_search?query=${encodeURIComponent(searchQuery)}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        const matches = response.data.mongo_data?.matches || [];
        const suggestions = [...new Set(matches.map((match) => match.matched_text))];
        setSearchSuggestions(suggestions);
        setLoading(false);
      } catch (error) {
        handleError(error, "Unable to fetch search suggestions: ");
      }
    }, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchQuery, navigate, handleError]);

  useEffect(() => {
    const pollingIntervals = {};
    const pollStatus = async (referenceId) => {
      try {
        const token = localStorage.getItem("authToken");
        if (!token) throw new Error("No authentication token found.");
        const response = await axios.get(
          `${API_BASE_URL}/documents/status/${referenceId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        const progress = response.data.progress_percentage || 0;
        setPollingProgress((prev) => Math.max(prev, progress));
        let allThumbnailsLoaded = true;

        const updatedFilesPromises = uploadedFiles.map(async (file) => {
          if (file.reference_id === referenceId) {
            const document = response.data.documents?.find(
              (doc) => doc.pdf_file_name === file.name
            );
            if (document) {
              const thumbnailLoaded =
                document.thumbnail_path &&
                file.thumbnail &&
                file.thumbnail !== "/default_thumb.jpg";
              if (!thumbnailLoaded) allThumbnailsLoaded = false;

              let thumbnail = file.thumbnail;
              if (document.thumbnail_path) {
                thumbnail = `${API_BASE_URL}/thumbnails/${document.thumbnail_path}`;
              } else if (!thumbnail || thumbnail === "/default_thumb.jpg") {
                thumbnail = await generateThumbnail(file.fileData, referenceId, file.name);
              }

              return {
                ...file,
                initialData: document.initial_data || null,
                documentId: document.document_id || file.documentId,
                thumbnail,
                transaction_set_id: document.transaction_set_id?.toString() || file.transaction_set_id,
                isSaved: file.isSaved || false,
              };
            }
          }
          return file;
        });

        const updatedUploadedFiles = await Promise.all(updatedFilesPromises);
        setUploadedFiles(updatedUploadedFiles);

        if (
          selectedFileIndex !== null &&
          updatedUploadedFiles[selectedFileIndex]?.reference_id === referenceId
        ) {
          const newInitialData = updatedUploadedFiles[selectedFileIndex].initialData || null;
          setExtractedData(newInitialData);
          setOriginalExtractedData(newInitialData);
          setDocumentId(updatedUploadedFiles[selectedFileIndex].documentId || null);
        }

        if (progress === 100 || allThumbnailsLoaded) {
          clearInterval(pollingIntervals[referenceId]);
          setPollingReferenceIds((prev) => {
            const newSet = new Set(prev);
            newSet.delete(referenceId);
            return newSet;
          });
          if (!progressComplete) {
            setProgressComplete(true);
            setTimeout(() => {
              setSimulatedProgress(0);
              setProgressComplete(false);
            }, 3000);
            toast.success(
              "Documents have been processed successfully. Click Save on each document to add them to the transaction.",
              {
                position: "bottom-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                className: "toast-success",
              }
            );
          }
        }
      } catch (error) {
        clearInterval(pollingIntervals[referenceId]);
        setPollingReferenceIds((prev) => {
          const newSet = new Set(prev);
          newSet.delete(referenceId);
          return newSet;
        });
        setPollingProgress(0);
        setSimulatedProgress(0);
        setProgressComplete(false);
        toast.error("Failed to check document status.", {
          position: "bottom-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          className: "toast-error",
        });
      }
    };

    pollingReferenceIds.forEach((referenceId) => {
      pollingIntervals[referenceId] = setInterval(() => pollStatus(referenceId), 5000);
    });

    return () =>
      Object.values(pollingIntervals).forEach((interval) => clearInterval(interval));
  }, [
    pollingReferenceIds,
    uploadedFiles,
    selectedFileIndex,
    progressComplete,
    setPollingProgress,
    setPollingReferenceIds,
    setProgressComplete,
    setUploadedFiles,
    setExtractedData,
    setOriginalExtractedData,
    setDocumentId,
    generateThumbnail,
  ]);

  const isSavedDisabledExisting = (file, index) => {
    return (
      userRole !== "admin" ||
      !isDataChanged ||
      selectedFileType !== "existing" ||
      selectedFileIndex !== index
    );
  };

  const isSavedDisabledUploaded = (file, index) => {
    return !file.initialData || !file.documentId || file.isSaved;
  };

  const filteredTransactions = isSearchActive
    ? transactions
    : transactions.filter((t) =>
        t.name.toLowerCase().includes(searchQuery.toLowerCase())
      );

  // Check if all uploaded files are saved
  const areAllUploadedFilesSaved = uploadedFiles.length === 0 || uploadedFiles.every((file) => file.isSaved);

  return (
    <div className="category-section">
       <Typography variant="h6" gutterBottom className="newt-title">
              Existing Transactions
            </Typography>
      {!selectedTransaction ? (
        <>
          <div className="existing-transaction-search">
            <TextField
              fullWidth
              placeholder="Search Transactions"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              variant="outlined"
              sx={{ mb: 4, maxWidth: 600 }}
            />
            {searchSuggestions.length > 0 && searchQuery.trim() && (
              <Box
                sx={{
                  border: "1px solid",
                  borderColor: "var(--border-color)",
                  borderRadius: 1,
                  bgcolor: "var(--card-background)",
                  backdropFilter: "var(--backdrop-blur)",
                  boxShadow: "var(--box-shadow)",
                  maxHeight: 250,
                  overflowY: "auto",
                  position: "absolute",
                  zIndex: 1000,
                  width: "100%",
                  maxWidth: 600,
                }}
              >
                {searchSuggestions.map((suggestion, index) => (
                  <Box
                    key={index}
                    sx={{
                      p: 1,
                      cursor: "pointer",
                      transition: "var(--transition-ease)",
                      "&:hover": { 
                        bgcolor: "var(--border-color)",
                      },
                    }}
                    onClick={() => handleSuggestionClick(suggestion)}
                  >
                    <Typography variant="body2" sx={{ color: "var(--text-color)" }}>
                      {suggestion}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
            {searchMessage && (
              <Typography
                variant="body2"
                className="existing-transaction-placeholder"
                sx={{
                  mt: 1,
                  color: searchMessage.includes("error") ? "var(--error-color)" : "var(--text-muted)",
                }}
              >
                {searchMessage}
              </Typography>
            )}
            {loading && (
              <Box className="existing-transaction-loading" sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}
          </div>
<div className="existing-transaction-folders">
  {filteredTransactions.length === 0 && !error && !loading && !searchMessage ? (
    <Typography className="existing-transaction-placeholder">No transactions found.</Typography>
  ) : (
    <div 
        className="transaction-folder-container"
        style={{
          display: 'flex',
          flexWrap: 'nowrap',
          overflowX: 'auto',
          overflowY: 'visible',
          width: '100%',
          gap: '2rem',
          padding: '1rem 0'
        }}
      >
      {filteredTransactions.map((t, index) => (
        <Box
          key={t.id}
          className="transaction-folder-wrapper"
          onMouseEnter={() => handleMouseEnter(t, index)}
          onMouseLeave={handleMouseLeave}
        >
          <Box
            className="transaction-folder"
            onClick={() => handleTransactionSelect(t.id)}
          >
            <div className="document-icon">
              <div className="page bottom" />
              <div className="page middle" />
              <div className="page top">
                <img
                  src={t.thumbnail || "/default_thumb.jpg"}
                  alt={t.name}
                  className="folder-thumbnail"
                />
              </div>
            </div>
          </Box>
          <Typography
            variant="subtitle2"
            className="folder-name"
            onClick={() => handleTransactionSelect(t.id)}
            sx={{ 
              fontSize: '0.7rem !important',
              lineHeight: '1.3 !important',
              whiteSpace: 'normal !important',
              wordWrap: 'break-word !important',
            }}
          >
            {truncateFileName(t.name)} ({t.files.length} file{t.files.length !== 1 ? "s" : ""})
          </Typography>
          {hoveredTransaction === t.id && previewThumbnails.length > 0 && (
            <div
              className="preview-panel"
              style={{ zIndex: 1000 - index }}
              onMouseEnter={() => setHoveredTransaction(t.id)}
              onMouseLeave={handleMouseLeave}
            >
              {previewThumbnails.map((item, idx) => (
                <div
                  key={idx}
                  className="preview-item"
                  onClick={() => handleThumbnailClick(t.id, item.fileIndex)}
                >
                  <img
                    src={item.thumbnail || "/default_thumb.jpg"}
                    alt={item.name}
                    className="preview-thumbnail"
                  />
                  <Typography
                    variant="caption"
                    className="preview-name"
                  >
                    {truncateFileName(item.name)}
                  </Typography>
                </div>
              ))}
            </div>
          )}
        </Box>
      ))}
    </div>
  )}
</div>
        </>
      ) : (
        <div className="transaction-details">
          <Box sx={{ mb: 2 }} className="existing-transaction-back">
            
          </Box>
          <div className="existing-transaction-inputs">
            <ProgressBar
              progress={simulatedProgress}
              isProcessing={pollingReferenceIds.size > 0}
              hasFiles={uploadedFiles.length > 0}
            />
            <DragAndDrop
              onFileChange={handleFileChange}
              fileInputRef={fileInputRef}
              promptText="Add New File"
              buttonText="Browse Files"
              isExistingTransaction={true}
              disabled={pollingReferenceIds.size > 0}
            />
          </div>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 2,
              mt: 2,
            }}
          >
            <Box sx={{ flex: 1, maxWidth: { md: "50%" }, minHeight: 400 }}>
              <Typography variant="subtitle1" gutterBottom>
                PDF Preview
              </Typography>
              {pdfUrl ? (
                <PdfPreview pdfUrl={pdfUrl} />
              ) : (
                <Typography className="existing-transaction-placeholder">No PDF selected.</Typography>
              )}
            </Box>
            <Box sx={{ flex: 1, maxWidth: { md: "50%" } }}>
              <Typography variant="subtitle1" gutterBottom>
                Extracted Data
              </Typography>
              {loading ? (
                <Box className="existing-transaction-loading" sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                  <CircularProgress />
                </Box>
              ) : error ? (
                <Typography color="error" className="existing-transaction-error">{error}</Typography>
              ) : extractedData ? (
                <>
                  <JsonEditor
                    jsonData={extractedData}
                    onChange={handleJsonChange}
                    isNew={false}
                    isAdmin={userRole === "admin"}
                    transactionId={selectedTransaction}
                    documentId={documentId}
                    fromCustomerProfiling={false}
                    onEditChange={setIsEditing}
                  />
                  {userRole === "admin" && isDataChanged && (
                    <Box className="existing-transaction-buttons" sx={{ mt: 2, display: "flex", gap: 1 }}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleUpdateData}
                      >
                        Update
                      </Button>
                    </Box>
                  )}
                </>
              ) : (
                <Typography className="existing-transaction-placeholder">No data available for the selected file.</Typography>
              )}
            </Box>
          </Box>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Transaction Files
            </Typography>
            {loading ? (
              <Box className="existing-transaction-loading" sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                {console.log("Rendering Transaction Files - Selected Transaction:", selectedTransaction, "Transactions:", transactions)}
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3, mt: 2 }}>
                  {transactions.find((t) => t.id === selectedTransaction)?.files?.length > 0 ? (
                    transactions
                      .find((t) => t.id === selectedTransaction)
                      .files.map((file, index) => (
                        <FileCard
                          key={file.id}
                          file={file}
                          index={index}
                          selectedFileIndex={selectedFileType === "existing" ? selectedFileIndex : null}
                          onSelect={() => handleFileSelect(index, selectedTransaction)}
                          onDelete={() => handleDeleteDocument(selectedTransaction, file.id, file.name)}
                          onSave={() => handleUpdateData()}
                          isSavedDisabled={isSavedDisabledExisting}
                        />
                      ))
                  ) : (
                    <Typography className="existing-transaction-placeholder">No existing files available for this transaction.</Typography>
                  )}
                  {uploadedFiles.length > 0 &&
                    uploadedFiles.map((file, index) => (
                      <FileCard
                        key={`${file.id}-${index}`}file={file}
                        index={index}
                        selectedFileIndex={selectedFileType === "uploaded" ? selectedFileIndex : null}
                        onSelect={() => handleUploadedFileSelect(index)}
                        onDelete={() => handleDeleteUploadedFile(file.id, file.name, index)}
                        onSave={() => handleSaveDocument(file, index)}
                        isSavedDisabled={isSavedDisabledUploaded}
                      />
                    ))}
                </Box>
              </>
            )}
            <Box sx={{ width: "100%", textAlign: "center", mt: 2 }}>
              <Button 
                className="newt-button newt-button-primary" 
                onClick={handleCompleteTransaction}
                disabled={!hasFileChanges || (uploadedFiles.length > 0 && !areAllUploadedFilesSaved)}
              >
                Complete Transaction 
              </Button>
            </Box> 
          </Box>
        </div>
      )}
    </div>
  );
};

export default ExistingTransaction;